import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { School, Group, Class as ClassIcon, PublishedWithChanges } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from 'recharts';
import { api } from '../../../shared/api';
import { formatINR } from '../../../shared/utils';
import PageHeader from '../../../shared/PageHeader';

export default function PrincipalDashboard() {
  const { data } = useQuery({ queryKey: ['principal-dashboard'], queryFn: async () => (await api.get('/dashboard/principal')).data });

  return (
    <Box>
      <PageHeader title="Principal's Desk" subtitle="School-wide academic and operational health." />
      <Grid container spacing={2}>
        <Stat icon={<School />} color="#1E3A8A" label="Students" value={data?.students ?? 0} link="/portal/students" />
        <Stat icon={<Group />} color="#10B981" label="Teachers" value={data?.teachers ?? 0} link="/portal/teachers" />
        <Stat icon={<ClassIcon />} color="#F59E0B" label="Classes" value={data?.classes ?? 0} link="/portal/timetable" />
        <Stat icon={<PublishedWithChanges />} color="#EF4444" label="Pending publications" value={data?.pendingResultPublications ?? 0} link="/portal/results" />

        <Grid item xs={12} md={8}>
          <Card sx={{ height: 360 }}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Class-wise attendance %</Typography>
              <Box sx={{ height: 300, mt: 1 }}>
                <ResponsiveContainer>
                  <BarChart data={data?.classWiseAttendance ?? []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <RTooltip />
                    <Bar dataKey="percent" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 360 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Fee collection</Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stat2 label="Total due" value={formatINR(data?.feeCollection?.totalDue ?? 0)} />
                <Stat2 label="Collected" value={formatINR(data?.feeCollection?.totalPaid ?? 0)} color="success.main" />
                <Stat2 label="Outstanding" value={formatINR((data?.feeCollection?.totalDue ?? 0) - (data?.feeCollection?.totalPaid ?? 0))} color="error.main" />
                <Stat2 label="Collected %" value={`${data?.feeCollection?.percent ?? 0}%`} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function Stat({ icon, color, label, value, link }: any) {
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card component={RouterLink} to={link} sx={{ textDecoration: 'none', display: 'block' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ color, bgcolor: `${color}10`, p: 1, borderRadius: 2 }}>{icon}</Box>
            <Box>
              <Typography variant="overline" color="text.secondary">{label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
function Stat2({ label, value, color }: any) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 700, color }}>{value}</Typography>
    </Stack>
  );
}
